import React, { useEffect, useState } from "react";
import { Plus, Trash2, Edit, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api, brl, fileUrl, formatApiError } from "@/lib/api";
import ImageUpload from "@/components/admin/ImageUpload";

const DEFAULT_SIZES = ["14", "P", "M", "G", "GG", "EXG", "G1", "G2"];

const EMPTY = {
  name: "", description: "", price: 0, photos: [],
  sizes: DEFAULT_SIZES.map((l) => ({ label: l, available: true })),
  measurements: "", fabric: "", fit: "", delivery_note: "", is_cap: false, active: true, sort_order: 0,
};

export default function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/products?include_inactive=true").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name) { toast.error("Informe o nome do produto."); return; }
    setSaving(true);
    try {
      const payload = { ...editing, price: Number(editing.price) || 0, sort_order: Number(editing.sort_order) || 0 };
      if (editing.id) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Produto salvo!");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este produto?")) return;
    await api.delete(`/products/${id}`);
    load();
  };

  const setPhoto = (idx, url) => {
    const photos = [...(editing.photos || [])];
    if (url) photos[idx] = url; else photos.splice(idx, 1);
    setEditing({ ...editing, photos: photos.filter(Boolean) });
  };

  const toggleSize = (label) => {
    const sizes = editing.sizes.map((s) => s.label === label ? { ...s, available: !s.available } : s);
    setEditing({ ...editing, sizes });
  };

  if (editing) {
    return (
      <div className="max-w-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-head font-bold text-xl">{editing.id ? "Editar produto" : "Novo produto"}</h2>
          <Button variant="ghost" onClick={() => setEditing(null)}><X size={18} /></Button>
        </div>
        <div className="card-surface p-6 space-y-4">
          <div><Label>Nome *</Label><Input data-testid="product-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bg-[var(--bg-secondary)]" /></div>
          <div><Label>Descrição</Label><Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="bg-[var(--bg-secondary)]" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Preço (R$)</Label><Input data-testid="product-price" type="number" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="bg-[var(--bg-secondary)]" /></div>
            <div><Label>Ordem</Label><Input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} className="bg-[var(--bg-secondary)]" /></div>
          </div>

          <div>
            <Label>Fotos</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              {(editing.photos || []).map((p, i) => (
                <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[var(--border)]">
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setPhoto(i, "")} className="absolute top-1 right-1 bg-black/70 rounded-full p-0.5"><X size={12} /></button>
                </div>
              ))}
              <ImageUpload value="" onChange={(url) => setPhoto((editing.photos || []).length, url)} label="" />
            </div>
          </div>

          <div>
            <Label>Tamanhos e disponibilidade</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {editing.sizes.map((s) => (
                <button key={s.label} type="button" onClick={() => toggleSize(s.label)} data-testid={`toggle-size-${s.label}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-bold border-2 ${s.available ? "border-[var(--accent)] text-[var(--accent)]" : "border-[var(--border)] text-[var(--text-muted)] line-through"}`}>
                  {s.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-1">Clique para ativar/desativar cada tamanho.</p>
          </div>

          <div><Label>Tabela de medidas</Label><Textarea value={editing.measurements} onChange={(e) => setEditing({ ...editing, measurements: e.target.value })} className="bg-[var(--bg-secondary)]" placeholder="Ex: P: 50cm largura x 70cm altura..." /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tecido</Label><Input value={editing.fabric} onChange={(e) => setEditing({ ...editing, fabric: e.target.value })} className="bg-[var(--bg-secondary)]" /></div>
            <div><Label>Modelagem</Label><Input value={editing.fit} onChange={(e) => setEditing({ ...editing, fit: e.target.value })} className="bg-[var(--bg-secondary)]" /></div>
          </div>
          <div><Label>Entrega / retirada (nota)</Label><Input value={editing.delivery_note} onChange={(e) => setEditing({ ...editing, delivery_note: e.target.value })} className="bg-[var(--bg-secondary)]" /></div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2"><Switch checked={editing.is_cap} onCheckedChange={(v) => setEditing({ ...editing, is_cap: v })} data-testid="product-is-cap" /> É boné (item opcional)</label>
            <label className="flex items-center gap-2"><Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="product-active" /> Ativo</label>
          </div>

          <Button onClick={save} disabled={saving} data-testid="product-save" className="btn-primary font-bold w-full h-11">
            {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : null} Salvar produto
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-[var(--text-muted)]">{products.length} produto(s)</p>
        <Button onClick={() => setEditing({ ...EMPTY, sizes: DEFAULT_SIZES.map((l) => ({ label: l, available: true })) })} data-testid="add-product-button" className="btn-primary font-bold"><Plus size={16} className="mr-1" /> Novo produto</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} className="card-surface overflow-hidden" data-testid={`admin-product-${p.id}`}>
            <div className="aspect-video bg-[var(--bg-secondary)] overflow-hidden">
              {p.photos?.[0] && <img src={fileUrl(p.photos[0])} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2">
                <h3 className="font-bold flex-1">{p.name}</h3>
                {p.is_cap && <span className="text-[10px] bg-[var(--accent)] text-black px-1.5 py-0.5 rounded font-bold">BONÉ</span>}
                {!p.active && <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">inativo</span>}
              </div>
              <div className="text-[var(--accent)] font-bold mt-1">{brl(p.price)}</div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => setEditing({ ...EMPTY, ...p })} data-testid={`edit-product-${p.id}`} className="border-[var(--border)] flex-1"><Edit size={14} className="mr-1" /> Editar</Button>
                <Button size="sm" variant="outline" onClick={() => remove(p.id)} data-testid={`delete-product-${p.id}`} className="border-[var(--border)] text-red-400"><Trash2 size={14} /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
